import { useState } from 'react';
import Select from 'react-select';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/* -----------------------
   Condition options
----------------------- */
const CONDITION_OPTIONS = [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' }
];

const DEVICE_OPTIONS = [
    { value: 'desktop', label: 'Desktop' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'tablet', label: 'Tablet' }
];

const DeviceFilterModal = ({ isOpen, onClose, onApply }) => {
    // ✅ REQUIRED STATE
    const [condition, setCondition] = useState('is');
    const [selectedDevices, setSelectedDevices] = useState([]);

    const handleApply = () => {
        if (!selectedDevices.length) {
            onClose();
            return;
        }

        // join multi values with |
        const values = selectedDevices.map(d => d.value).join('|');

        const fParams = [
            `${condition},device,${values}`
        ];

        onApply(fParams);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={modalStyle}>
                {/* Header */}
                <div style={headerStyle}>
                    <h3 style={titleStyle}>Filter by Device</h3>
                    <button onClick={onClose} style={closeBtnStyle}>
                        <X size={18} />
                    </button>
                </div>

                {/* Device filter */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>Device</label>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        {/* Condition */}
                        <select
                            value={condition}
                            onChange={e => setCondition(e.target.value)}
                            style={selectStyle}
                        >
                            {CONDITION_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>

                        {/* Multi-select */}
                        <div style={{ flex: 1 }}>
                            <Select
                                options={DEVICE_OPTIONS}
                                value={selectedDevices}
                                onChange={val => setSelectedDevices(val || [])}
                                isMulti
                                isClearable
                                placeholder="Select devices"
                                styles={reactSelectStyles}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={footerStyle}>
                    <Button
                        onClick={handleApply}
                        disabled={!selectedDevices.length}
                        style={{ backgroundColor: '#6b7280', color: '#fff' }}
                    >
                        Apply filter
                    </Button>
                </div>
            </div>
        </div>
    );
};

/* -----------------------
   Styles
----------------------- */
const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
};

const modalStyle = {
    width: '460px',
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.04)'
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
};

const titleStyle = {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f172a',
    letterSpacing: '-0.01em'
};

const footerStyle = {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    justifyContent: 'flex-end'
};

const closeBtnStyle = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
    width: 28,
    height: 28,
    borderRadius: 9999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s, color 0.15s'
};

const labelStyle = {
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '8px',
    display: 'block'
};

const selectStyle = {
    padding: '10px 12px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    background: '#fff',
    color: '#0f172a',
    minWidth: '120px',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const reactSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        fontSize: 14,
        borderRadius: 12,
        borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
        boxShadow: state.isFocused
            ? '0 0 0 3px rgba(99, 102, 241, 0.12)'
            : '0 1px 2px rgba(15, 23, 42, 0.04)',
        '&:hover': { borderColor: state.isFocused ? '#6366f1' : '#cbd5e1' },
        transition: 'box-shadow 0.2s, border-color 0.2s'
    }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
    singleValue: (base) => ({ ...base, color: '#0f172a' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#94a3b8', padding: 6 }),
    menu: (base) => ({
        ...base,
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        color: '#0f172a',
        backgroundColor: state.isSelected ? '#eef2ff' : state.isFocused ? '#f8fafc' : 'transparent',
        cursor: 'pointer'
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#eef2ff',
        borderRadius: 9999,
        paddingLeft: 4,
        paddingRight: 2
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#4338ca',
        fontSize: 12,
        fontWeight: 500
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#4338ca',
        borderRadius: 9999,
        ':hover': { backgroundColor: '#c7d2fe', color: '#312e81' }
    })
};

export default DeviceFilterModal;
